// client/src/components/pages/ErrorDetection/index.tsx (converted from .js, ST-00216)
/**
 * Error Detection Dashboard
 * Industrial-teal monitoring aesthetic with severity KPIs,
 * rule cards, and comprehensive modal workflows.
 */

import { useState, useEffect, useCallback, type ComponentType } from 'react';
import { ShieldAlert, AlertTriangle, RefreshCw, Plus, Clock, Search, Filter } from 'lucide-react';
import type { AxiosResponse } from 'axios';
import api from '../../../services/api';
import { PageHeader, ThemedSelect, useAuth } from '../../../types/commonComponents';
import ErrorCard from './ErrorCard';
import ErrorDetailsModal from './ErrorDetailsModal';
import NotificationModal from './NotificationModal';
import ErrorRuleModal from './ErrorRuleModal';
import AlertLogModalJs from './AlertLogModal';
import { getSeverityConfig as getSeverityConfigJs } from './severityConfig';
import type {
  ErrorDetectionResult,
  ErrorDetectionRule,
  RunCheckResponse,
  SeverityStyle,
} from '../../../types/clientPages/errorDetection';

/** The rule a modal opens on: a results row, or the full rule fetched for editing. */
type SelectedRule = ErrorDetectionResult | ErrorDetectionRule;
type ToastType = 'success' | 'error';

// Typed views of the (still JavaScript) severity table and alert log.
const getSeverityConfig = getSeverityConfigJs as unknown as (severity: string) => SeverityStyle;
const AlertLogModal = AlertLogModalJs as unknown as ComponentType<{
  rule: SelectedRule;
  onClose: () => void;
}>;

/* ─── Severity KPI Card ──────────────────────── */
const SeverityKPI = ({
  severity,
  count,
  delay = 0,
}: {
  severity: string;
  count: number;
  delay?: number;
}) => {
  const config = getSeverityConfig(severity);
  const Icon = config.icon;

  return (
    <div
      className={`relative rounded-xl border ${config.border} ${config.bg} p-5 hover:shadow-card-hover transition-all duration-300 animate-fadeIn overflow-hidden`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2">
            {config.label}
          </p>
          <p
            className={`text-3xl font-bold tabular-nums ${config.text}`}
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
          >
            {count}
          </p>
        </div>
        <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${config.accent}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );
};

/* ─── Skeleton Loaders ───────────────────────── */
const KPISkeleton = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="rounded-xl border border-slate-200 bg-surface p-5 animate-pulse">
        <div className="flex items-start justify-between">
          <div>
            <div className="h-3 w-16 bg-slate-200 rounded mb-3" />
            <div className="h-8 w-12 bg-slate-200 rounded" />
          </div>
          <div className="w-10 h-10 bg-slate-200 rounded-lg" />
        </div>
      </div>
    ))}
  </div>
);

const CardSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
    {[0, 1, 2].map((i) => (
      <div key={i} className="rounded-xl border border-slate-200 bg-surface p-6 animate-pulse">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-200 rounded-lg" />
            <div>
              <div className="h-4 w-32 bg-slate-200 rounded mb-2" />
              <div className="h-3 w-48 bg-slate-200 rounded" />
            </div>
          </div>
        </div>
        <div className="h-16 bg-slate-100 rounded-lg mb-4" />
        <div className="flex gap-2">
          <div className="h-9 flex-1 bg-slate-200 rounded-lg" />
          <div className="h-9 flex-1 bg-slate-200 rounded-lg" />
          <div className="h-9 w-9 bg-slate-200 rounded-lg" />
        </div>
      </div>
    ))}
  </div>
);

/* ─── Main Component ─────────────────────────── */
const ErrorDetection = () => {
  // Rules are SQL the server runs: adding, editing, deleting and running them needs
  // errors.manage (the server enforces it — ST-00214). Everyone else sees results only.
  const { hasPermission } = useAuth();
  const canManage = hasPermission('errors.manage');
  const [errorResults, setErrorResults] = useState<ErrorDetectionResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [runningRuleIds, setRunningRuleIds] = useState<Set<number>>(new Set());
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showAlertLogModal, setShowAlertLogModal] = useState(false);
  const [selectedError, setSelectedError] = useState<SelectedRule | null>(null);
  const [detailsResult, setDetailsResult] = useState<ErrorDetectionResult | null>(null);

  // Toast helper
  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Fetch error detection results
  const fetchResults = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response: AxiosResponse<ErrorDetectionResult[]> = await api.errorDetection.getResults();
      setErrorResults(response.data);
      setLastChecked(new Date());
      setError(null);
    } catch (err) {
      console.error('Error fetching error detection results:', err);
      setError('Failed to load error detection results. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Run all checks
  const runAllChecks = async () => {
    try {
      setRefreshing(true);
      const response: AxiosResponse<RunCheckResponse> = await api.errorDetection.runCheck(null);
      await fetchResults(true);
      const failedRules = response.data?.results?.filter((r) => r.error) || [];
      if (failedRules.length > 0) {
        showToast(
          `${failedRules.length} rule(s) failed: ${failedRules.map((r) => r.ruleName).join(', ')}`,
          'error'
        );
      } else {
        showToast(
          `Checked ${response.data?.results?.length || errorResults.length} rule(s) successfully`
        );
      }
    } catch (err) {
      console.error('Error running checks:', err);
      showToast('Failed to run error detection checks', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  // Run single rule check
  const runSingleCheck = async (ruleId: number) => {
    try {
      setRunningRuleIds((prev) => new Set([...prev, ruleId]));
      const response: AxiosResponse<RunCheckResponse> = await api.errorDetection.runCheck(ruleId);
      await fetchResults(true);
      const failedRule = response.data?.results?.find((r) => r.error);
      if (failedRule) {
        showToast(`SQL error: ${failedRule.error}`, 'error');
      } else {
        showToast('Rule check completed');
      }
    } catch (err) {
      console.error('Error running check:', err);
      showToast('Failed to run check for this rule', 'error');
    } finally {
      setRunningRuleIds((prev) => {
        const next = new Set(prev);
        next.delete(ruleId);
        return next;
      });
    }
  };

  // Modal handlers
  const handleViewDetails = (errorResult: ErrorDetectionResult) => {
    setDetailsResult(errorResult);
    setShowDetailsModal(true);
  };

  const handleSetupNotification = (errorResult: ErrorDetectionResult) => {
    setSelectedError(errorResult);
    setShowNotificationModal(true);
  };

  const handleViewAlerts = (errorResult: ErrorDetectionResult) => {
    setSelectedError(errorResult);
    setShowAlertLogModal(true);
  };

  const handleAddRule = () => {
    setSelectedError(null);
    setShowRuleModal(true);
  };

  // Edit rule - fetch full rule data (including SQLQuery) then open modal
  const handleEditRule = async (errorResult: ErrorDetectionResult) => {
    try {
      const response: AxiosResponse<ErrorDetectionRule[]> = await api.errorDetection.getRules();
      const fullRule = response.data.find((r) => r.rule_id === errorResult.rule_id);
      if (fullRule) {
        setSelectedError(fullRule);
        setShowRuleModal(true);
      } else {
        showToast('Could not load rule details', 'error');
      }
    } catch (err) {
      console.error('Error fetching rule for edit:', err);
      showToast('Failed to load rule for editing', 'error');
    }
  };

  // Delete rule
  const handleDeleteRule = async (ruleId: number) => {
    try {
      await api.errorDetection.deleteRule(ruleId);
      await fetchResults(true);
      showToast('Rule deleted successfully');
    } catch (err) {
      console.error('Error deleting rule:', err);
      showToast('Failed to delete rule', 'error');
    }
  };

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Compute severity counts
  const severityCounts = errorResults.reduce<Record<string, number>>(
    (acc, r) => {
      const sev = r.severity_level || 'none';
      acc[sev] = (acc[sev] || 0) + 1;
      return acc;
    },
    { none: 0, warning: 0, critical: 0, armageddon: 0 }
  );

  // Filter + search
  const filteredResults = errorResults.filter((r) => {
    const matchesSearch =
      !searchQuery ||
      r.rule_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSeverity = filterSeverity === 'all' || r.severity_level === filterSeverity;
    return matchesSearch && matchesSeverity;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[100] px-5 py-3 rounded-xl shadow-lg text-sm font-medium animate-slideIn ${
            toast.type === 'error'
              ? 'bg-red-600 text-white'
              : 'bg-slate-800 text-white dark:text-slate-50'
          }`}
        >
          {toast.message}
        </div>
      )}

      <PageHeader
        icon={ShieldAlert}
        title="Error Detection"
        subtitle="Monitor data quality rules and severity thresholds"
        actions={
          <>
            {lastChecked && (
              <div className="hidden lg:flex items-center gap-1.5 text-xs text-slate-400">
                <Clock className="w-3.5 h-3.5" />
                {lastChecked.toLocaleTimeString()}
              </div>
            )}
            {canManage && (
              <button
                type="button"
                onClick={runAllChecks}
                disabled={refreshing || loading}
                aria-label={refreshing ? 'Checking...' : 'Run All'}
                title={refreshing ? 'Checking...' : 'Run All'}
                className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 sm:px-3.5 border border-slate-300 bg-surface text-slate-700 hover:border-slate-400 hover:bg-slate-50 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw
                  className={`w-4 h-4 text-slate-500 ${refreshing ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
                <span className="page-header__label">{refreshing ? 'Checking...' : 'Run All'}</span>
              </button>
            )}
            {canManage && (
              <button
                type="button"
                onClick={handleAddRule}
                aria-label="Add Rule"
                title="Add Rule"
                className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 sm:px-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                <Plus className="w-4 h-4" aria-hidden="true" />
                <span className="page-header__label">Add Rule</span>
              </button>
            )}
          </>
        }
      />

      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6">
        {/* Severity KPI Strip */}
        {loading ? (
          <KPISkeleton />
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {['none', 'warning', 'critical', 'armageddon'].map((severity, idx) => (
              <SeverityKPI
                key={severity}
                severity={severity}
                count={severityCounts[severity] || 0}
                delay={idx * 60}
              />
            ))}
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="flex items-center gap-3 p-4 rounded-xl border border-red-200 bg-red-50 animate-fadeIn">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
            <button
              onClick={() => fetchResults()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        )}

        {/* Search & Filter Bar */}
        {!loading && errorResults.length > 0 && (
          <div
            className="flex flex-col sm:flex-row gap-3 animate-fadeIn"
            style={{ animationDelay: '120ms' }}
          >
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search rules..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-slate-200 bg-surface text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <ThemedSelect
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
                className="pl-9 pr-8 py-2.5 rounded-lg border border-slate-200 bg-surface text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 appearance-none cursor-pointer transition-all"
              >
                <option value="all">All Severities</option>
                <option value="none">OK</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
                <option value="armageddon">Armageddon</option>
              </ThemedSelect>
            </div>
          </div>
        )}

        {/* Rule Cards Grid */}
        {loading ? (
          <CardSkeleton />
        ) : filteredResults.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filteredResults.map((errorResult, idx) => (
              <ErrorCard
                key={errorResult.rule_id}
                errorResult={errorResult}
                canManage={canManage}
                onViewDetails={handleViewDetails}
                onSetupNotification={handleSetupNotification}
                onRunCheck={runSingleCheck}
                onDeleteRule={handleDeleteRule}
                onEditRule={handleEditRule}
                onViewAlerts={handleViewAlerts}
                isRunning={runningRuleIds.has(errorResult.rule_id)}
                delay={idx * 50}
              />
            ))}
          </div>
        ) : errorResults.length > 0 ? (
          /* No results after filter */
          <div className="text-center py-16 animate-fadeIn">
            <Search className="mx-auto w-10 h-10 text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-600">No rules match your filters</p>
            <p className="text-xs text-slate-400 mt-1">
              Try adjusting your search or severity filter
            </p>
          </div>
        ) : (
          /* Empty state - no rules at all */
          <div className="text-center py-20 animate-fadeIn">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-100 mb-4">
              <ShieldAlert className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-1">No error detection rules</h3>
            <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
              Create your first rule to start monitoring data quality across your operations.
            </p>
            {canManage && (
              <button
                onClick={handleAddRule}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors shadow-sm"
              >
                <Plus className="w-4 h-4" />
                Create First Rule
              </button>
            )}
          </div>
        )}

        {/* Modals */}
        {showDetailsModal && detailsResult && (
          <ErrorDetailsModal
            errorResult={detailsResult}
            onClose={() => setShowDetailsModal(false)}
          />
        )}

        {showNotificationModal && selectedError && (
          <NotificationModal
            errorResult={selectedError}
            onClose={() => setShowNotificationModal(false)}
            onSave={() => {
              setShowNotificationModal(false);
              showToast('Notification saved');
            }}
          />
        )}

        {showRuleModal && (
          <ErrorRuleModal
            editRule={selectedError}
            onClose={() => {
              setShowRuleModal(false);
              setSelectedError(null);
            }}
            onSave={() => {
              const wasEditing = !!selectedError;
              setShowRuleModal(false);
              setSelectedError(null);
              fetchResults(true);
              showToast(wasEditing ? 'Rule updated successfully' : 'Rule created successfully');
            }}
          />
        )}

        {showAlertLogModal && selectedError && (
          <AlertLogModal
            rule={selectedError}
            onClose={() => {
              setShowAlertLogModal(false);
              setSelectedError(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ErrorDetection;
